import { Injectable } from '@angular/core';
import { Observable, catchError, map, shareReplay, throwError } from 'rxjs';
import { AsociacionesService, CargosService, EjerciciosService } from '../../api';

export interface OpcionAsociacion { id: number; nombre: string; /** 0 FSJ, 1 Barraca, 2 Hoguera */ tipo: number | null; activa: boolean; }
export interface OpcionCargo { id: number; nombre: string; esInfantil: boolean; activo: boolean; }
export interface OpcionEjercicio { id: number; anyo: number; vigente: boolean; }

/** Tipos de asociación (valores reales de tipos_asociaciones). */
export const TIPOS_ASOCIACION: { id: number; texto: string }[] = [
  { id: 0, texto: 'FSJ (federación)' },
  { id: 1, texto: 'Barraca' },
  { id: 2, texto: 'Hoguera' }
];

export function textoTipoAsociacion(tipo: number | null | undefined): string {
  return TIPOS_ASOCIACION.find(t => t.id === tipo)?.texto ?? (tipo === null || tipo === undefined ? '' : 'Tipo ' + tipo);
}

const porNombre = (a: { nombre: string }, b: { nombre: string }) => String(a.nombre).localeCompare(String(b.nombre), 'es');

/**
 * Catálogos pequeños (ejercicios ≈20, cargos ≈25, asociaciones ≈180) que necesitan los selectores y filtros del Histórico y de la
 * ficha de Ejercicio. Se piden solo cuando una pantalla los necesita, se guardan en memoria para la sesión y un fallo NO se cachea
 * (se puede reintentar). No incluye asociados: esos se buscan bajo demanda en la API.
 */
@Injectable({ providedIn: 'root' })
export class CatalogosHistoricoService {
  private asociaciones?: Observable<OpcionAsociacion[]>;
  private cargos?: Observable<OpcionCargo[]>;
  private ejercicios?: Observable<OpcionEjercicio[]>;

  constructor(
    private readonly asociacionesApi: AsociacionesService,
    private readonly cargosApi: CargosService,
    private readonly ejerciciosApi: EjerciciosService
  ) {}

  private cachear<T>(origen: Observable<T>, limpiar: () => void): Observable<T> {
    return origen.pipe(catchError(err => { limpiar(); return throwError(() => err); }), shareReplay({ bufferSize: 1, refCount: false }));
  }

  asociacionesOrdenadas(): Observable<OpcionAsociacion[]> {
    return this.asociaciones ??= this.cachear(
      this.asociacionesApi.asociacionesGet().pipe(map(res => (res.asociaciones ?? [])
        .map(a => ({ id: a.id as number, nombre: String(a.nombre ?? ''), tipo: a['tipoAsociacion'] ?? null, activa: Number(a['active']) !== 0 }))
        .sort(porNombre))),
      () => { this.asociaciones = undefined; });
  }

  cargosOrdenados(): Observable<OpcionCargo[]> {
    return this.cargos ??= this.cachear(
      this.cargosApi.cargosGet().pipe(map(res => (res.cargos ?? [])
        .filter(c => Number(c.id) > 0 && String(c.nombre ?? '').trim() !== '')
        .map(c => ({ id: c.id as number, nombre: String(c.nombre), esInfantil: Number(c['esInfantil']) === 1, activo: Number(c['active']) !== 0 }))
        .sort(porNombre))),
      () => { this.cargos = undefined; });
  }

  /** Ejercicios del más reciente al más antiguo. El vigente es el que tiene `active = 1` (no se asume el año natural). */
  ejerciciosRecientes(): Observable<OpcionEjercicio[]> {
    return this.ejercicios ??= this.cachear(
      this.ejerciciosApi.ejerciciosGet().pipe(map(res => (res.ejercicios ?? [])
        .map(e => ({ id: e.id as number, anyo: Number(e.ejercicio), vigente: Number(e.active) === 1 }))
        .sort((a, b) => b.anyo - a.anyo))),
      () => { this.ejercicios = undefined; });
  }
}
